import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { fetchThreatActorDataByID as getThreatActorService} from "services/modules/ecs/data";

import { Button,  CardContent, Card, CardHeader, Divider} from "@mui/material";

function ViewThreatActorPage() {
  // use location hook
  const location = useLocation();

  // Query params and values
  const searchParams = new URLSearchParams(location.search);
  const threat = searchParams.get("threat");

  // State variables
  const [threatactor, setThreatactor] = useState({});

  // Fetches data based on threat id
  useEffect(() => {
    const fetchData = async () => {
      const params = { id: threat };
      const response = await getThreatActorService({ params });
      setThreatactor(response.data);
    };

    if (threat) {
      fetchData();
    }
  }, [threat]);
  
  // Renders the values incase of nested objects and arrays
  const renderPropertyValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(", ");
    } else if (typeof value === "object" && value !== null) {
      return (
        <div>
          {Object.entries(value).map(([key, val], index) => (
            <div key={index}>
              <strong>{formatPropertyName(key)}:</strong>{" "}
              {renderPropertyValue(val)}
            </div>
          ))}
        </div>
      );
    } else if (typeof value === "string") {
      return value.split("\n").map((line, index, array) => (
        <React.Fragment key={index}>
          {line.trim()}
          {index < array.length - 1 && <br />}
        </React.Fragment>
      ));
    } else {
      return value.toString();
    }
  };
  
  // Handles the logic when the property is either tactics or techniques
  const renderTTPsValue = (key, value) => {
    if (key === "tactics" || key === "techniques") {
      return value.join(", ");
    }
    return value.toString();
  };
  
  // Formats the property name
  function formatPropertyName(property) {
    return property.replace(/_/g, " ").replace(/(?:^|\s)\S/g, function (a) {
      return a.toUpperCase();
    });
  }

  const orderedProperties = [
    "actor_name",
    "aliases",
    "geo_origin",
    "primary_motivations",
    "targeted_countries",
    "targeted_industries",
    "recent_activity",
    "links",
  ];

  const properties = Object.keys(threatactor)
    .filter((property) => property !== "id" && property !== "common_ttps")
    .sort((a, b) => {
      const orderA = orderedProperties.indexOf(a);
      const orderB = orderedProperties.indexOf(b);
      if (orderA === -1 && orderB === -1) return a.localeCompare(b);
      if (orderA === -1) return 1;
      if (orderB === -1) return -1;
      return orderA - orderB;
    });

  return (
    <>
      <Card>
        <CardHeader
          title={
            <React.Fragment>
              <div className="card-head">
                <div className="flex-head">
                  <label className="label">{threatactor.actor_name}</label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    className="back font"
                    onClick={()=>window.history.back()}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <Divider className="divider" />
        <div>
          <CardContent>
            <div className="row" id="single-alert">
              <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
                <Card>
                  <CardContent>
                    {threatactor &&
                      properties.map(
                        (property, index) =>
                          property !== "actor_name" &&
                          property !== "attacks" &&
                          property !== "mitigation" &&
                          property !== "ioc" &&
                          property !== "links" && (
                            <React.Fragment key={index}>
                              <div className="row">
                                <div className="col-4 text-body1">
                                  <strong className="word-wrap">
                                    {property === "ThreatActor"
                                      ? "Threat Actor"
                                      : formatPropertyName(property)}
                                  </strong>
                                  :
                                </div>
                                <div
                                  className={`col-8 text-body1 ${
                                    property === "domain"
                                      ? "text-secondary"
                                      : property === "cve" ||
                                        property === "ThreatActor"
                                      ? "text-primary"
                                      : "text-grey-8"
                                  }`}
                                >
                                  {renderPropertyValue(threatactor[property])}
                                </div>
                              </div>
                            </React.Fragment>
                          )
                      )}
                  </CardContent>
                </Card>
              </div>
              <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
                <Card>
                  <label className="px-4 label">Common TTP's</label>
                  <CardContent>
                    {threatactor &&
                      threatactor.common_ttps &&
                      Object.keys(threatactor.common_ttps)
                        .filter(
                          (key) => key !== "recent_activity" && key !== "links"
                        )
                        .map((key, index) => (
                          <React.Fragment key={index}>
                            <div className="row">
                              <div className="col-4 text-body1">
                                <strong className="word-wrap">
                                  {formatPropertyName(key)}
                                </strong>
                                :
                              </div>
                              <div className="col-8 text-body1 text-grey-8">
                                {renderTTPsValue(
                                  key,
                                  threatactor.common_ttps[key]
                                )}
                              </div>
                            </div>
                          </React.Fragment>
                        ))
                    }
                    {
                      threatactor && threatactor.mitigation &&
                      (
                        <div className="row">
                          <div className="col-4 text-body1">
                            <strong className="word-wrap">
                              {formatPropertyName("mitigation")}
                            </strong>
                            :
                          </div>
                          <div className="col-8 text-body1 text-grey-8">
                            {renderTTPsValue(
                              "mitigation",
                              threatactor.mitigation
                            )}
                          </div>
                        </div>
                      )
                    }
                  </CardContent>
                </Card>
              </div>
            </div>
            <div className="row mt-5">
              <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
                <Card>
                  <label className="px-4 label">Cyber Attacks</label>
                  <CardContent>
                    {threatactor?.attacks?.length > 0 ? (
                      threatactor.attacks.map((attack, index) => (
                        <div className="row">
                          {Object.entries(
                            attack
                          ).map(
                            ([key, value], index) =>
                              key !== "newslink" && (
                                <div className="row" key={index}>
                                  <div className="col-4 text-body1">
                                    <strong className="word-wrap">
                                      {formatPropertyName(key)}
                                    </strong>
                                    :
                                  </div>
                                  <div className="col-8 text-body1 text-grey-8">
                                    {renderPropertyValue(value)}
                                  </div>
                                </div>
                              )
                          )}
                      </div>
                      ))
                    ) : (
                      <div className="px-4 text-body1 text-grey-8">No attacks found.</div>
                    )}
                  </CardContent>
                </Card>
              </div>
              <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
                <Card>
                  <label className="px-4 label">IOC's</label>
                  <CardContent>
                  {threatactor?.ioc && Object.keys(threatactor.ioc).length > 0 ? (
                      Object.keys(threatactor.ioc).map((key, index) => (
                        <React.Fragment key={index}>
                          <div className="row" role="listitem">
                            <div className="col-4 text-body1">
                              <strong className="word-wrap">{formatPropertyName(key)}</strong>:
                            </div>
                            <div className="col-8 text-body1 text-grey-8">
                              {renderTTPsValue(key, threatactor.ioc[key])}
                            </div>
                          </div>
                        </React.Fragment>
                      ))
                    ) : (
                      <div>No Indicators of Compromise available on this ThreatActor.</div>
                    )}

                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="row mt-5">
              <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
                <label className="px-4 label">References</label>
                {threatactor &&
                  threatactor.common_ttps &&
                  Object.keys(threatactor.common_ttps)
                    .filter((key) => key === "links")
                    .map((key, index) => (
                      <React.Fragment key={index}>
                        <div className="col-12 text-body1 text-grey-8 px-4 mt-2">
                          <span className="black">{index + 1}.</span>
                          <a
                            className="news-links"
                            href={renderTTPsValue(
                              key,
                              threatactor.common_ttps[key]
                            )}
                          >
                            {renderTTPsValue(key, threatactor.common_ttps[key])}
                          </a>
                        </div>
                      </React.Fragment>
                    ))}
                {threatactor &&
                  threatactor.common_ttps &&
                  threatactor.common_ttps.recent_activity &&
                  threatactor.common_ttps.recent_activity.newslink && (
                    <React.Fragment>
                      <div className="col-12 text-body1 text-grey-8 px-4 mt-2">
                        <span className="black">
                          {Object.keys(threatactor.common_ttps).filter(
                            (key) => key === "links"
                          ).length + 1}
                          .
                        </span>
                        <a
                          className="news-links"
                          href={
                            threatactor.common_ttps.recent_activity.newslink
                          }
                        >
                          {threatactor.common_ttps.recent_activity.newslink}
                        </a>
                      </div>
                    </React.Fragment>
                  )}
              </div>
            </div>
          </CardContent>
        </div>
      </Card>
    </>
  );
}

export default ViewThreatActorPage;
